import { notFound, redirect } from 'next/navigation'
import { eq, and, desc } from 'drizzle-orm'
import { auth } from '@/auth'
import { db } from '@/db'
import { tickets, ticketMessages, aiArtifacts, projects, users } from '@/db/schema'
import { transition } from '@/lib/ticket-state-machine'
import { loadTenantConfig } from '@/lib/tenant-config-loader'
import { canAnswerClarificationQuestions, canSeeClarificationWaitingState } from '@/lib/ticket-detail-actions'
import { groupTicketMessages } from '@/lib/ticket-message-display'
import { getDraftUiState } from '@/lib/ticket-draft-ui'
import path from 'path'
import ClarificationForm from './ClarificationForm'
import CloseTicketButton from './CloseTicketButton'
import DraftResponseButton from './DraftResponseButton'
import DraftPoller from './DraftPoller'
import ExpertReplyForm from './ExpertReplyForm'
import ExpertResponseForm from './ExpertResponseForm'
import FollowUpForm from './FollowUpForm'
import TagEditor from './TagEditor'

const CONFIG_PATH = path.resolve(process.cwd(), 'tenant.config.yaml')

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted — analyzing…',
  NEEDS_INFO: 'More information needed',
  TRIAGED: 'Triaged — awaiting expert',
  AWAITING_EXPERT: 'Ready for review',
  EXPERT_REVIEWING: 'In expert review',
  RESPONSE_DRAFTING: 'Preparing response…',
  RESPONSE_READY: 'Response ready',
  RESPONDED: 'Expert has responded',
  AWAITING_REPLY: 'Awaiting your reply',
  CLOSED: 'Closed',
  ARCHIVED: 'Archived',
}

const ACTIVE_EXPERT_RESPONSE_STATUSES = [
  'SUBMITTED',
  'NEEDS_INFO',
  'TRIAGED',
  'AWAITING_EXPERT',
  'EXPERT_REVIEWING',
  'RESPONSE_DRAFTING',
  'RESPONSE_READY',
  'AWAITING_REPLY',
  'RESPONDED',
]

export default async function TicketPage({
  params,
}: {
  params: Promise<{ ticketId: string }>
}) {
  const session = await auth()
  if (!session?.user?.id) redirect('/login')

  const { ticketId } = await params

  const tenantId = session.user.tenantId
  const role = session.user.role
  if (!tenantId) redirect('/login')

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, ticketId), eq(tickets.tenantId, tenantId)))
    .limit(1)

  const canViewTicket =
    ticket && (ticket.submitterId === session.user.id || ['expert', 'admin'].includes(role ?? ''))

  if (!canViewTicket) notFound()

  const isExpert = ['expert', 'admin'].includes(role ?? '')
  let visibleTicket = ticket

  if (isExpert && ticket.status === 'AWAITING_EXPERT') {
    const nextStatus = transition(ticket.status, 'EXPERT_REVIEWING')
    const [updatedTicket] = await db
      .update(tickets)
      .set({
        status: nextStatus,
        assignedExpertId: session.user.id,
        version: ticket.version + 1,
        updatedAt: new Date(),
      })
      .where(and(eq(tickets.id, ticket.id), eq(tickets.version, ticket.version)))
      .returning()

    visibleTicket = updatedTicket ?? ticket
  }

  const messages = await db
    .select({
      id: ticketMessages.id,
      role: ticketMessages.role,
      bodyRaw: ticketMessages.bodyRaw,
      bodySanitized: ticketMessages.bodySanitized,
      createdAt: ticketMessages.createdAt,
      authorName: users.displayName,
      authorEmail: users.email,
    })
    .from(ticketMessages)
    .leftJoin(users, eq(ticketMessages.authorId, users.id))
    .where(eq(ticketMessages.ticketId, ticketId))
    .orderBy(ticketMessages.createdAt)

  // Fetch the latest clarification_questions artifact if NEEDS_INFO
  const [clarificationArtifact] =
    visibleTicket.status === 'NEEDS_INFO'
      ? await db
          .select()
          .from(aiArtifacts)
          .where(
            and(
              eq(aiArtifacts.ticketId, ticketId),
              eq(aiArtifacts.artifactType, 'clarification_questions'),
            ),
          )
          .orderBy(desc(aiArtifacts.createdAt))
          .limit(1)
      : []

  const questions: string[] = clarificationArtifact?.contentJson
    ? JSON.parse(clarificationArtifact.contentJson).questions ?? []
    : []

  const [summaryArtifact] = await db
    .select()
    .from(aiArtifacts)
    .where(and(eq(aiArtifacts.ticketId, ticketId), eq(aiArtifacts.artifactType, 'summary')))
    .orderBy(desc(aiArtifacts.createdAt))
    .limit(1)

  const [draftArtifact] = await db
    .select()
    .from(aiArtifacts)
    .where(and(eq(aiArtifacts.ticketId, ticketId), eq(aiArtifacts.artifactType, 'suggested_response')))
    .orderBy(desc(aiArtifacts.createdAt))
    .limit(1)

  const draftJson = draftArtifact?.contentJson
    ? JSON.parse(draftArtifact.contentJson) as {
        sources?: Array<{ index: number; title: string; slug: string; score?: number }>
        knowledge_gaps?: string[]
      }
    : null
  const draftUiState = getDraftUiState({
    isExpert,
    status: visibleTicket.status,
    hasDraft: Boolean(draftArtifact),
  })
  const hasClarificationQuestions = questions.length > 0
  const showClarificationForm = canAnswerClarificationQuestions({
    isExpert,
    status: visibleTicket.status,
    currentUserId: session.user.id,
    submitterId: visibleTicket.submitterId,
    hasQuestions: hasClarificationQuestions,
  })
  const showClarificationWaitingState = canSeeClarificationWaitingState({
    isExpert,
    status: visibleTicket.status,
    hasQuestions: hasClarificationQuestions,
  })
  const canExpertReply = isExpert && ACTIVE_EXPERT_RESPONSE_STATUSES.includes(visibleTicket.status)
  const showExpertFollowUpReply = canExpertReply && Boolean(visibleTicket.resolutionSeverity)
  const showExpertManualResponse =
    canExpertReply && !visibleTicket.resolutionSeverity && !draftArtifact && draftUiState !== 'generating'
  const messageGroups = groupTicketMessages(messages)

  // Load project name for the submission details panel
  let projectName: string | null = null
  if (visibleTicket.projectId) {
    const [project] = await db
      .select({ name: projects.name })
      .from(projects)
      .where(eq(projects.id, visibleTicket.projectId))
      .limit(1)
    projectName = project?.name ?? null
  }

  // Load S-field options from tenant config
  const tenantConfig = await loadTenantConfig(CONFIG_PATH)
  const severityOptions = tenantConfig.intake.resolution_severity_levels
  const concernOptions = tenantConfig.intake.resolution_concern_types

  return (
    <main className="min-h-screen bg-zinc-50 px-4 py-12 text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 font-mono mb-1">{visibleTicket.referenceNumber}</p>
          <h1 className="text-xl font-semibold text-zinc-950 dark:text-white">
            {visibleTicket.title ?? 'Issue submitted'}
          </h1>
          <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium bg-sky-50 text-sky-800 border border-sky-200 dark:bg-sky-400/10 dark:text-sky-200 dark:border-sky-400/30">
            {STATUS_LABELS[visibleTicket.status] ?? visibleTicket.status}
          </span>
          {visibleTicket.aiProcessingState === 'degraded' && (
            <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">
              AI processing is unavailable. An expert will review your ticket manually.
            </p>
          )}
          {isExpert && ['RESPONDED', 'AWAITING_REPLY'].includes(visibleTicket.status) && (
            <div className="mt-3">
              <CloseTicketButton ticketId={ticketId} version={visibleTicket.version} />
            </div>
          )}
        </div>

        {isExpert && summaryArtifact ? (
          <section className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">AI Summary</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-zinc-700 dark:text-zinc-300">
              {summaryArtifact.content}
            </p>
          </section>
        ) : null}

        {/* Submission details — read-only R fields, visible to both roles */}
        {(visibleTicket.department || visibleTicket.issueCategory || visibleTicket.affectedSystem) && (
          <section className="rounded-lg border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
            <dl className="grid grid-cols-[minmax(7.5rem,auto)_1fr] gap-x-4 gap-y-1.5 text-xs leading-5 sm:grid-cols-[minmax(8.5rem,auto)_1fr_minmax(8.5rem,auto)_1fr]">
              {projectName && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Project</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{projectName}</dd>
                </>
              )}
              {visibleTicket.dateOfOccurrence && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Date of occurrence</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{visibleTicket.dateOfOccurrence}</dd>
                </>
              )}
              {visibleTicket.department && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Department</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{visibleTicket.department}</dd>
                </>
              )}
              {visibleTicket.issueCategory && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Issue category</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{visibleTicket.issueCategory}</dd>
                </>
              )}
              {visibleTicket.affectedSystem && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Affected system</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{visibleTicket.affectedSystem}</dd>
                </>
              )}
              {visibleTicket.userPriority && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Declared priority</dt>
                  <dd className="capitalize text-zinc-950 dark:text-zinc-100">{visibleTicket.userPriority}</dd>
                </>
              )}
              {visibleTicket.actionRequested && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400">Action requested</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100">{visibleTicket.actionRequested}</dd>
                </>
              )}
              {visibleTicket.successCriteria && (
                <>
                  <dt className="font-medium text-zinc-500 dark:text-zinc-400 sm:col-start-1 sm:col-end-2">Success criteria</dt>
                  <dd className="text-zinc-950 dark:text-zinc-100 sm:col-start-2 sm:col-end-5">{visibleTicket.successCriteria}</dd>
                </>
              )}
            </dl>
          </section>
        )}

        {/* Message thread */}
        <div className="space-y-3">
          {messageGroups.map((group) => (
            <div
              key={group.id}
              className={`rounded-lg border p-4 text-sm ${
                group.role === 'line_manager'
                  ? 'bg-white border-zinc-200 text-zinc-950 dark:bg-zinc-900 dark:border-zinc-800 dark:text-zinc-100'
                  : group.role === 'expert'
                    ? 'bg-sky-50 border-sky-200 text-sky-950 dark:bg-sky-400/10 dark:border-sky-400/30 dark:text-sky-100'
                    : 'bg-zinc-100 border-zinc-200 text-zinc-600 italic dark:bg-zinc-900/60 dark:border-zinc-800 dark:text-zinc-400'
              }`}
            >
              <p className="mb-3 text-xs font-medium text-zinc-500 dark:text-zinc-400">{group.authorLabel}</p>
              <div className="space-y-4">
                {group.items.map((msg) => (
                  <div key={msg.id} className="space-y-3">
                    {msg.clarificationAnswers ? (
                      msg.clarificationAnswers.map((item, index) => (
                        <div key={`${msg.id}-${index}`} className="space-y-1">
                          <p className="text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                            {index + 1}. {item.question}
                          </p>
                          <p className="whitespace-pre-wrap text-sm leading-6">{item.answer}</p>
                        </div>
                      ))
                    ) : (
                      <p className="whitespace-pre-wrap text-sm leading-6">{msg.displayText}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* NEEDS_INFO clarification form — only the submitter answers these questions. */}
        {showClarificationForm && (
          <ClarificationForm ticketId={ticketId} questions={questions} />
        )}

        {showClarificationWaitingState && (
          <section className="rounded-lg border border-amber-300 bg-amber-50 p-5 dark:border-amber-400/40 dark:bg-amber-300/10">
            <h2 className="text-sm font-semibold text-amber-950 dark:text-amber-100">
              Waiting for line manager input
            </h2>
            <p className="mt-2 text-sm leading-6 text-amber-900 dark:text-amber-100/90">
              The AI requested additional information from the line manager before this ticket is routed for an expert response.
            </p>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-100/90">
              {questions.map((question) => (
                <li key={question}>{question}</li>
              ))}
            </ul>
          </section>
        )}

        {showExpertManualResponse ? (
          <section className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
              <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">Expert override response</h2>
              <p className="mt-1 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
                You can respond now, even if AI analysis, draft generation, or clarification is still in progress.
              </p>
            </div>
            <ExpertResponseForm
              ticketId={ticketId}
              initialDraft=""
              initialSummary={summaryArtifact?.content ?? null}
              severityOptions={severityOptions}
              concernOptions={concernOptions}
            />
          </section>
        ) : null}

        {/* RESPONDED — line manager can follow up with the expert */}
        {!isExpert && visibleTicket.status === 'RESPONDED' && (
          <FollowUpForm ticketId={ticketId} context="follow_up" />
        )}

        {/* AWAITING_REPLY — line manager clarification or follow-up */}
        {!isExpert && visibleTicket.status === 'AWAITING_REPLY' && (
          <FollowUpForm
            ticketId={ticketId}
            context={visibleTicket.resolutionSeverity ? 'follow_up' : 'awaiting_reply'}
          />
        )}

        {/* Experts can send follow-up replies after an initial response has been recorded. */}
        {showExpertFollowUpReply && (
          <ExpertReplyForm ticketId={ticketId} />
        )}

        {/* Experts can request a draft once they are reviewing the ticket. */}
        {draftUiState === 'requestable' ? (
          <section className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">Response draft</h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                  Generate a KB-grounded draft response for expert review.
                </p>
              </div>
              <DraftResponseButton ticketId={ticketId} />
            </div>
          </section>
        ) : null}

        {/* Auto-poll only while a draft job has actually been queued. */}
        {draftUiState === 'generating' ? (
          <>
            <DraftPoller />
            <p className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-100">
              Generating response draft… this usually takes a few seconds.
            </p>
          </>
        ) : null}

        {/* Draft form — shown whenever a draft exists, regardless of exact status */}
        {isExpert && draftArtifact ? (
          <section className="space-y-4">
            <div className="rounded-lg border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">KB Sources</h2>
                <DraftResponseButton ticketId={ticketId} label="Regenerate draft" />
              </div>
              {draftJson?.sources?.length ? (
                <ul className="mt-2 space-y-2 text-sm text-zinc-700 dark:text-zinc-300">
                  {draftJson.sources.map((source) => (
                    <li key={source.index} className="break-all">
                      [{source.index}] {source.title}
                      <span className="text-zinc-500 dark:text-zinc-400"> · {source.slug}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">No KB sources were used.</p>
              )}
              {draftJson?.knowledge_gaps?.length ? (
                <div className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 dark:border-amber-400/40 dark:bg-amber-300/10">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-100">
                    Knowledge gaps
                  </h3>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-900 dark:text-amber-100/90">
                    {draftJson.knowledge_gaps.map((gap) => (
                      <li key={gap}>{gap}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
            <ExpertResponseForm
              ticketId={ticketId}
              initialDraft={draftArtifact.content}
              initialSummary={summaryArtifact?.content ?? null}
              severityOptions={severityOptions}
              concernOptions={concernOptions}
            />
          </section>
        ) : null}

        {isExpert ? (
          <TagEditor ticketId={ticketId} initialTags={visibleTicket.categoryTags ?? []} />
        ) : null}
      </div>
    </main>
  )
}
